<template>
    <common-modal
        v-if="isOpen && selectedReferral"
        width="medium"
        :heading="$t('modals.patient.medical.steps.view')"
        @close="$emit('close')"
    >
        <div class="view-details-modal">
            <div class="details-header">
                <div class="details-title-row">
                    <span class="tag" :class="getStepTagColor({ referral: selectedReferral })">
                        {{ getStepTag({ referral: selectedReferral }) }}
                    </span>
                    <span class="details-type">{{ getStepTitle({ referral: selectedReferral }) }}</span>
                </div>
            </div>

            <div class="details-body">
                <div class="details-group">
                    <h4 class="group-title">{{ $t('modals.patient.medical.view_modal.provider_practice') }}</h4>
                    <div class="group-content">
                        <div class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.provider') }}</span>
                            <span class="detail-value">
                                {{ getProviderFullName(selectedReferral.fromUser) || $t('modals.patient.medical.view_modal.na') }}
                            </span>
                        </div>
                        <div v-if="selectedReferral.fromUser?.specialities?.length" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.speciality') }}</span>
                            <span class="detail-value">{{ getStepSpeciality(selectedReferral.fromUser) }}</span>
                        </div>
                        <div v-if="selectedReferral.fromPractice?.name" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.practice') }}</span>
                            <span class="detail-value">{{ selectedReferral.fromPractice.name }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="selectedReferral.patient" class="details-group">
                    <h4 class="group-title">{{ $t('modals.patient.medical.view_modal.patient_info') }}</h4>
                    <div class="group-content">
                        <div class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.name') }}</span>
                            <span class="detail-value">
                                {{ selectedReferral.patient.firstName }} {{ selectedReferral.patient.lastName }}
                            </span>
                        </div>
                        <div v-if="selectedReferral.patient.dateOfBirth" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.dob') }}</span>
                            <span class="detail-value">{{ formatDate(selectedReferral.patient.dateOfBirth) }}</span>
                        </div>
                        <div v-if="selectedReferral.patient.email" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.email') }}</span>
                            <span class="detail-value">{{ selectedReferral.patient.email }}</span>
                        </div>
                    </div>
                </div>

                <div class="details-group">
                    <h4 class="group-title">{{ $t('modals.patient.medical.view_modal.appointment_location') }}</h4>
                    <div class="group-content">
                        <div class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.date_time') }}</span>
                            <span class="detail-value">
                                {{ formatDate(selectedReferral.appointmentDateTime || selectedReferral.createdDateTime, true) }}
                            </span>
                        </div>
                        <div v-if="getStepLocation({ referral: selectedReferral })" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.location') }}</span>
                            <span class="detail-value">{{ getStepLocation({ referral: selectedReferral }) }}</span>
                        </div>
                    </div>
                </div>

                <div class="details-group">
                    <h4 class="group-title">{{ $t('modals.patient.medical.view_modal.clinical_info') }}</h4>
                    <div class="group-content">
                        <div class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.diagnosis') }}</span>
                            <span class="detail-value">
                                {{ selectedReferral.diagnosis || $t('modals.patient.medical.view_modal.na') }}
                            </span>
                        </div>
                        <div v-if="selectedReferral.insurancePlan?.name" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.insurance_plan') }}</span>
                            <span class="detail-value">{{ selectedReferral.insurancePlan.name }}</span>
                        </div>
                        <div v-if="selectedReferral.stageInfo?.nextAction" class="detail-item">
                            <span class="detail-label">{{ $t('modals.patient.medical.view_modal.labels.status_details') }}</span>
                            <span class="detail-value">{{ selectedReferral.stageInfo.nextAction }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'

    export default {
        props: {
            isOpen: { type: Boolean, default: false },
            selectedReferral: { type: Object, default: null },
        },
        methods: {
            formatDate(date, includeTime = false) {
                if (!date) return 'N/A'
                const d = new Date(date)
                if (isNaN(d.getTime())) return date
                const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                if (!includeTime) return dateStr
                const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                return `${dateStr} ${timeStr}`
            },
            getStepTitle(step) {
                if (step?.referral?.followUpReferral) {
                    return this.$t('modals.patient.medical.steps.followUp')
                }
                const type = this.getStepType(step?.referral?.sourceType)
                return `${type} ${this.$t('modals.patient.medical.steps.referral')}`
            },
            getStepType(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return this.$t('pages.referrals.index.referrals_made.type_direct')
                if (type === ReferralTrackingTypeEnum.MATCH) return this.$t('pages.referrals.index.referrals_made.type_matched')
                return 'N/A'
            },
            getStepTag(step) {
                const displayStatus = this.getDisplayStatus(step?.referral?.status, step?.referral?.sourceType)
                return this.$t(`pages.referrals.tracking.status_title.${displayStatus}`)
            },
            getStepTagColor(step) {
                const displayStatus = this.getDisplayStatus(step?.referral?.status, step?.referral?.sourceType)
                const yellowStatuses = ['CREATED', 'AWAITING_CONFIRMATION']
                const blueStatuses = ['SCHEDULED', 'COMPLETED']
                const redStatuses = ['DECLINED', 'CANCELLED', 'NO_SHOW', 'EXPIRED']
                const greenStatuses = ['CONSULTATION_NOTES_PENDING']
                if (yellowStatuses.includes(displayStatus)) return 'yellow'
                if (blueStatuses.includes(displayStatus)) return 'blue'
                if (redStatuses.includes(displayStatus)) return 'today'
                if (greenStatuses.includes(displayStatus)) return 'green'
                return 'gray'
            },
            getDisplayStatus(status, sourceType) {
                const statusMapping = {
                    [ReferralTrackingTypeEnum.MATCH]: {
                        [ReferralStatusEnum.CREATED]: 'CREATED',
                        [ReferralStatusEnum.TIME_SLOTS_PROPOSED]: 'AWAITING_CONFIRMATION',
                        [ReferralStatusEnum.DECLINED]: 'DECLINED',
                        [ReferralStatusEnum.SCHEDULED]: 'SCHEDULED',
                        [ReferralStatusEnum.INACTIVE]: 'EXPIRED',
                    },
                    [ReferralTrackingTypeEnum.DIRECT]: {
                        [ReferralStatusEnum.PATIENT_VISIT_PENDING]: 'SCHEDULED',
                        [ReferralStatusEnum.PATIENT_VISIT_PAST]: 'NO_SHOW',
                        [ReferralStatusEnum.NO_SHOW]: 'NO_SHOW',
                        [ReferralStatusEnum.CANCELLED]: 'CANCELLED',
                        [ReferralStatusEnum.CONSULTATION_NOTES_PENDING]: 'CONSULTATION_NOTES_PENDING',
                        [ReferralStatusEnum.COMPLETED]: 'COMPLETED',
                    },
                }
                return statusMapping[sourceType]?.[status] || status
            },
            getStepSpeciality(user) {
                if (!user || !user.specialities || user.specialities.length === 0) return ''
                return user.specialities[0]?.speciality?.name || ''
            },
            getProviderFullName(user) {
                if (!user) return ''
                const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')
                const designation = user.designation || ''
                return [fullName, designation].filter(Boolean).join(', ')
            },
            getStepLocation(step) {
                const location = step.referral?.practiceLocation
                return location && [location.name, location.address, location.state, location.zip].filter(Boolean).join(', ')
                    ? [location.name, location.address, location.state, location.zip].filter(Boolean).join(', ')
                    : ''
            },
        },
    }
</script>

<style scoped lang="scss">
    .view-details-modal {
        padding: 4px 0;
    }
    .details-header {
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e5e7eb;
    }
    .details-title-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .details-type {
        font-size: 15px;
        font-weight: 600;
        color: #111827;
    }
    .details-body {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .details-group {
        background: #f9fafb;
        border-radius: 8px;
        padding: 12px;
        border: 1px solid #f3f4f6;
    }
    .group-title {
        font-size: 12px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    .group-content {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .detail-item {
        display: flex;
        justify-content: space-between;
        font-size: 13px;
    }
    .detail-label {
        color: #6b7280;
        font-weight: 500;
    }
    .detail-value {
        color: #111827;
        font-weight: 500;
        text-align: right;
    }
    .tag {
        display: inline-flex;
        align-items: center;
        border: 1px solid #ddd;
        padding: 1.75px 7px;
        border-radius: 6.75px;
        font-size: 12px;
        font-weight: 500;
        &.blue {
            color: #2563eb;
            background-color: #e6f0ff;
            border-color: #93c5fd;
        }
        &.yellow {
            background-color: #fefce8;
            color: #a65f00;
            border-color: #fde047;
        }
        &.today {
            background-color: #fef2f2;
            color: #c10007;
            border-color: #ffc9c9;
        }
        &.green {
            background-color: #f0fdf4;
            color: #008236;
            border-color: #b9f8cf;
        }
        &.gray {
            background-color: #f3f4f6;
            color: #364153;
            border-color: #e5e7eb;
        }
    }
</style>
